import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ToolSidebar from "../utils/ToolSideBar";
import { API_URL, authFetch } from "../utils/auth";
import "../../css/superuser/StaffDirectoryPage.css";
import "../../css/staff/InternalWorkspace.css";

function formatDate(value) {
  if (!value) return "No date set";
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function DashboardMetric({ metric }) {
  return (
    <article className="staff-card metric-card command-metric">
      <span>{metric.label}</span>
      <strong>{metric.value}</strong>
      {metric.meta ? <small>{metric.meta}</small> : null}
    </article>
  );
}

function Section({ title, to, action = "View all", children, empty }) {
  return (
    <section className="staff-card command-section">
      <div className="staff-card-header command-section-header">
        <h2>{title}</h2>
        {to ? <Link to={to}>{action}</Link> : null}
      </div>
      {children || <p className="staff-empty inline">{empty}</p>}
    </section>
  );
}

function AttentionList({ items = [] }) {
  return (
    <Section title="Needs Attention" empty="Nothing urgent right now.">
      {items.length ? (
        <div className="command-attention-list">
          {items.map((item) => (
            <Link className={`command-attention-item tone-${item.tone || "info"}`} to={item.href || "/dashboard"} key={`${item.label}-${item.href}`}>
              <strong>{item.count}</strong>
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
      ) : null}
    </Section>
  );
}

function ProjectRows({ projects = [] }) {
  if (!projects.length) return null;
  return projects.map((project) => (
    <Link className="command-row project-row" to={`/projects/${project.id}`} key={project.id}>
      <div>
        <strong>{project.project_name}</strong>
        <span>{project.business_name || project.client_name}</span>
      </div>
      <div><span>Status</span><strong>{project.delivery_status_label}</strong></div>
      <div><span>Health</span><strong className={`health-text health-${project.health}`}>{project.health_label}</strong></div>
      <div><span>PM</span><strong>{project.project_manager}</strong></div>
      <div><span>Team</span><strong>{project.team_names?.length ? project.team_names.join(", ") : "Unassigned"}</strong></div>
      <div><span>Target</span><strong>{formatDate(project.target_launch_date)}</strong></div>
    </Link>
  ));
}

function WorkboardOverview({ workboard }) {
  if (!workboard) return null;
  return (
    <Section title="Workboard Overview" to="/workboard" empty="No open tickets.">
      <div className="command-buckets">
        {(workboard.buckets || []).map((bucket) => <div key={bucket.label}><strong>{bucket.count}</strong><span>{bucket.label}</span></div>)}
      </div>
      {(workboard.tickets || []).length ? workboard.tickets.map((ticket) => (
        <Link className="command-row compact-row" to={`/workboard/tickets/${ticket.id}`} key={ticket.id}>
          <div>
            <strong>{ticket.title}</strong>
            <span>{ticket.ticket_key} · {ticket.team_name} · {ticket.project_name}</span>
          </div>
          <span>{ticket.priority_label}</span>
          <span>{ticket.status_label}</span>
        </Link>
      )) : null}
    </Section>
  );
}

function ApprovalRows({ approvals = [] }) {
  if (!approvals.length) return null;
  return approvals.map((item) => (
    <Link className="command-row compact-row" to={`/approvals/${item.id}`} key={item.id}>
      <div>
        <strong>{item.title}</strong>
        <span>{item.team_name} · {item.project_name}</span>
      </div>
      <span>{item.request_type_label}</span>
      <span>{item.decision_status_label}</span>
    </Link>
  ));
}

function TeamRows({ teams = [] }) {
  if (!teams.length) return null;
  return teams.map((team) => (
    <Link className="command-row team-row" to={`/teams/${team.id}`} key={team.id}>
      <div>
        <strong>{team.name}</strong>
        <span>{team.current_project}</span>
      </div>
      <div><span>PM</span><strong>{team.project_manager}</strong></div>
      <div><span>Members</span><strong>{team.member_count}</strong></div>
      <div><span>Health</span><strong>{team.health_label}</strong></div>
      <div><span>Rating</span><strong>{team.rating || "N/A"}</strong></div>
    </Link>
  ));
}

function PeopleSummary({ people = {} }) {
  return (
    <Section title="People & Hiring" to="/employees">
      <div className="people-summary-grid">
        <div><strong>{people.active_employees || 0}</strong><span>Active Employees</span></div>
        <div><strong>{people.open_applications || 0}</strong><span>Open Applications</span></div>
        <div><strong>{people.offers_awaiting_review || 0}</strong><span>Offers Awaiting Review</span></div>
        <div><strong>{people.personnel_approvals || 0}</strong><span>Personnel Approvals</span></div>
      </div>
    </Section>
  );
}

function PersonnelRows({ requests = [] }) {
  if (!requests.length) return null;
  return requests.map((item) => (
    <div className="command-row compact-row" key={item.id}>
      <div>
        <strong>{item.employee}</strong>
        <span>{item.team} · {item.label}</span>
      </div>
      <span>{item.status_label}</span>
      <span>{formatDate(item.created_at)}</span>
    </div>
  ));
}

function ActivityRows({ activity = [] }) {
  if (!activity.length) return null;
  return activity.map((item) => (
    <div className="command-activity-row" key={item.id}>
      <div className="activity-copy">
        <strong>{item.title || item.action_label}</strong>
        <span>{item.action_label} · {item.project_name} · {item.actor}</span>
      </div>
      <time>{formatDateTime(item.created_at)}</time>
    </div>
  ));
}

function RoleDashboard({ data }) {
  const header = data.header || {};
  const isHr = data.dashboard_type === "hr";
  return (
    <>
      <header className="staff-header internal-hero command-hero">
        <span>{header.eyebrow}</span>
        <div>
          <h1>{header.heading || "Dashboard"}</h1>
          <p>{header.body}</p>
        </div>
        {header.updated_at ? <time>Updated {formatDateTime(header.updated_at)}</time> : null}
      </header>

      <section className="internal-metrics command-metrics">
        {(data.summary_cards || []).map((metric) => <DashboardMetric metric={metric} key={metric.label} />)}
      </section>

      <div className="command-layout">
        <div className="command-main">
          <AttentionList items={data.attention || []} />
          {!isHr ? <Section title="Delivery Overview" to="/projects" empty="No active projects."><ProjectRows projects={data.projects || []} /></Section> : null}
          {!isHr ? <WorkboardOverview workboard={data.workboard} /> : null}
          {isHr ? <Section title="Personnel Requests" to="/approvals" empty="No personnel requests."><PersonnelRows requests={data.personnel_requests || []} /></Section> : null}
          <Section title="Recent Activity" empty="No recent activity."><ActivityRows activity={data.activity || []} /></Section>
        </div>
        <aside className="command-side">
          {!isHr ? <Section title="Pending Approvals" to="/approvals" empty="No pending approvals."><ApprovalRows approvals={data.approvals || []} /></Section> : null}
          <Section title="Teams" to="/teams" empty="No active teams."><TeamRows teams={data.teams || []} /></Section>
          <PeopleSummary people={data.people || {}} />
        </aside>
      </div>
    </>
  );
}

function DeliveryDashboard({ data, role }) {
  const metrics = data?.metrics || {};
  return (
    <>
      <header className="staff-header internal-hero">
        <span>SCD Team</span>
        <h1>{role === "project_manager" ? "Project management" : "Internal workspace"}</h1>
        <p>Scoped delivery work, client requests, milestones, and activity.</p>
      </header>
      <section className="internal-metrics">
        {Object.entries(metrics).map(([key, value]) => <article className="staff-card metric-card" key={key}><span>{key.replaceAll("_", " ")}</span><strong>{value}</strong></article>)}
      </section>
      <section className="internal-grid-two">
        <div className="staff-card"><div className="staff-card-header"><h2>Active Projects</h2><Link to="/projects">View all</Link></div>{(data.projects || []).length ? data.projects.map((project) => <Link className="workspace-row" to={`/projects/${project.id}`} key={project.id}><strong>{project.project_name}</strong><span>{project.health_label} · {project.delivery_status_label}</span></Link>) : <p className="staff-empty inline">No projects assigned.</p>}</div>
        <div className="staff-card"><h2>My Tasks</h2>{(data.tasks || []).length ? data.tasks.map((task) => <div className="workspace-row" key={task.id}><strong>{task.title}</strong><span>{task.status_label} · {task.due_date || "No due date"}</span></div>) : <p className="staff-empty inline">No tasks assigned.</p>}</div>
      </section>
      <section className="internal-grid-two">
        <div className="staff-card"><h2>Upcoming Milestones</h2>{(data.milestones || []).length ? data.milestones.map((item) => <div className="workspace-row" key={item.id}><strong>{item.name}</strong><span>{item.status_label}</span></div>) : <p className="staff-empty inline">No upcoming milestones.</p>}</div>
        <div className="staff-card"><h2>Recent Activity</h2>{(data.activity || []).length ? data.activity.map((item) => <div className="workspace-row" key={item.id}><strong>{item.summary || item.action}</strong><span>{item.actor} · {new Date(item.created_at).toLocaleString()}</span></div>) : <p className="staff-empty inline">No recent activity.</p>}</div>
      </section>
    </>
  );
}

export default function StaffDashboard({ role }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const response = await authFetch(`${API_URL}/staff/dashboard/`);
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.detail || "Unable to load staff dashboard.");
        setData(payload);
      } catch (err) {
        setError(err.message || "Unable to load staff dashboard.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div className="staff-shell">
      <ToolSidebar active="dashboard" />
      <main className="staff-page internal-page command-dashboard-page">
        {loading ? <section className="staff-card"><p>Loading dashboard...</p></section> : error ? <section className="staff-card"><p>{error}</p></section> : data?.dashboard_type === "superuser" || data?.dashboard_type === "hr" ? <RoleDashboard data={data} /> : <DeliveryDashboard data={data || {}} role={role} />}
      </main>
    </div>
  );
}
